// OPEN RAILINGS (owner 2026-10-16: "the sidewalls should be easier … paths
// should go over wooden bridges. and no bridges should have solid side
// walls"). The bake emits every deck / fill / well railing as ONE collide-only
// quad per straight piece (game/structures.js railQuad: kind parapet,
// WALL | TWO_SIDED | NO_RENDER, its bottom line a -> b with the drop on the
// right, (ox, oz); params emit.deck.railing): what the body feels. This file
// draws it — PURE (no three: structplan's chunk plan counts the triangles,
// structs2 writes them into the lit batch's thin tail, no draw call of its
// own):
//   railingChains  a group's railing quads joined end to end (same height,
//                  same material) and merged where the line runs straight
//                  (D.merge_m), one chain per run of railing
//   railingGeom    posts every D.styles[style].pitch_m along a chain (one at
//                  each end) + its rails, on the OUTER side of the felt face
//                  (the face is the line, the body stops there): the style by
//                  the quad's material (D.style_of_mat) —
//                    guard  W-beam guard rail on posts (road classes, 0.75 m)
//                    pipe   slim dark steel posts + two pipes (rail decks)
//                    wood   timber posts + two rails (paths, steps, tracks)
//                  tri(a, b, c, part) per triangle (part 'post' | 'rail'),
//                  -> the count (tri null: count only)
// D = structplan DRAW.railing.v.

const key = (p) => Math.round(p[0] * 1000) + ':' + Math.round(p[1] * 1000) + ':' + Math.round(p[2] * 1000);

// rails: game/structures.js railingsOf(...).rails; MATS: the material names
export function railingChains(rails, D, MATS) {
  const byStart = new Map();
  rails.forEach((r, i) => { const k = key(r.a) + '|' + r.mat; if (!byStart.has(k)) byStart.set(k, i); });
  const next = rails.map((r) => {
    const j = byStart.get(key(r.b) + '|' + r.mat);
    return j !== undefined && Math.abs(rails[j].h - r.h) < 1e-3 ? j : -1;
  });
  const hasPrev = new Uint8Array(rails.length);
  for (const j of next) if (j >= 0) hasPrev[j] = 1;
  const used = new Uint8Array(rails.length), chains = [];
  const walk = (i0) => {
    const pts = [rails[i0].a];
    let i = i0;
    for (; i >= 0 && !used[i]; i = next[i]) { used[i] = 1; pts.push(rails[i].b); }
    const name = MATS[rails[i0].mat], style = D.style_of_mat[name];
    if (!style || !D.styles[style]) throw new Error(`railing: no style for railing material ${name} (structplan DRAW.railing style_of_mat)`);
    chains.push({ pts: simplify(pts, D.merge_m), h: rails[i0].h, style });
  };
  rails.forEach((_, i) => { if (!hasPrev[i]) walk(i); });
  rails.forEach((_, i) => { if (!used[i]) walk(i); });
  return chains;
}

// drop an interior point within tol of the line between the kept ones
function simplify(P, tol) {
  if (P.length < 3) return P;
  const keep = [P[0]];
  let a = 0;
  const off = (p, u, v) => {
    const dx = v[0] - u[0], dy = v[1] - u[1], dz = v[2] - u[2], L2 = dx * dx + dy * dy + dz * dz;
    const t = L2 > 0 ? Math.max(0, Math.min(1, ((p[0] - u[0]) * dx + (p[1] - u[1]) * dy + (p[2] - u[2]) * dz) / L2)) : 0;
    return Math.hypot(u[0] + dx * t - p[0], u[1] + dy * t - p[1], u[2] + dz * t - p[2]);
  };
  for (let j = 2; j < P.length; j++) {
    let ok = true;
    for (let i = a + 1; i < j && ok; i++) ok = off(P[i], P[a], P[j]) <= tol;
    if (!ok) { keep.push(P[j - 1]); a = j - 1; }
  }
  keep.push(P[P.length - 1]);
  return keep;
}

// the out normal of segment i (the drop on the right of a -> b) and at
// point k (the mean of its segments')
function frames(P) {
  const n = P.length, seg = [], pt = [];
  for (let i = 0; i + 1 < n; i++) {
    const dx = P[i + 1][0] - P[i][0], dz = P[i + 1][2] - P[i][2], L = Math.hypot(dx, dz) || 1;
    seg.push([-dz / L, dx / L, dx / L, dz / L, L]);
  }
  for (let k = 0; k < n; k++) {
    const A = seg[Math.max(0, k - 1)], B = seg[Math.min(n - 2, k)];
    let x = A[0] + B[0], z = A[1] + B[1];
    const l = Math.hypot(x, z) || 1;
    x /= l; z /= l;
    // a joint's offset line keeps the section's width (1 / cos of the half turn)
    const c = Math.max(0.5, x * B[0] + z * B[1]);
    pt.push([x / c, z / c]);
  }
  return { seg, pt };
}

export function railingGeom(ch, D, tri = null) {
  const S = D.styles[ch.style], P = ch.pts, h = ch.h;
  if (P.length < 2) return 0;
  let n = 0;
  const T = tri ? (a, b, c, part) => { tri(a, b, c, part); n++; } : () => { n++; };
  const quad = (a, b, c, d, part) => { T(a, b, c, part); T(a, c, d, part); };
  const F = frames(P);
  // a rail: section corners (d out, y up) swept along the chain, faces
  // between consecutive corners (open: no closing face)
  const sweep = (sec, part) => {
    for (let i = 0; i + 1 < P.length; i++) {
      const A = P[i], B = P[i + 1], na = F.pt[i], nb = F.pt[i + 1];
      const at = (p, nn, c) => [p[0] + nn[0] * c[0], p[1] + c[1], p[2] + nn[1] * c[0]];
      for (let q = 0; q + 1 < sec.length; q++) quad(at(A, na, sec[q]), at(B, nb, sec[q]), at(B, nb, sec[q + 1]), at(A, na, sec[q + 1]), part);
    }
  };
  // a post at arc s: a square of side pw centred dc out of the line, its
  // top at h (top: a lid)
  const total = F.seg.reduce((t, s) => t + s[4], 0);
  const post = (s, pw, dc, ph, lid) => {
    let i = 0;
    while (i < F.seg.length - 1 && s > F.seg[i][4]) { s -= F.seg[i][4]; i++; }
    const g = F.seg[i], u = Math.min(1, s / g[4]), A = P[i], B = P[i + 1];
    const x = A[0] + (B[0] - A[0]) * u, y = A[1] + (B[1] - A[1]) * u, z = A[2] + (B[2] - A[2]) * u;
    const r = pw / 2, cx = x + g[0] * dc, cz = z + g[1] * dc;
    const c = [[-r, -r], [r, -r], [r, r], [-r, r]].map(([t, d]) => [cx + g[2] * t + g[0] * d, cz + g[3] * t + g[1] * d]);
    for (let k = 0; k < 4; k++) {
      const p = c[k], q = c[(k + 1) % 4];
      quad([p[0], y, p[1]], [q[0], y, q[1]], [q[0], y + ph, q[1]], [p[0], y + ph, p[1]], 'post');
    }
    if (lid) quad([c[0][0], y + ph, c[0][1]], [c[1][0], y + ph, c[1][1]], [c[2][0], y + ph, c[2][1]], [c[3][0], y + ph, c[3][1]], 'post');
  };
  // posts: one at each end, evenly between at most pitch_m apart
  const np = Math.max(1, Math.ceil(total / S.pitch_m - 1e-6));
  const dc = (S.post_d_m || 0) + S.post_w_m / 2;
  for (let k = 0; k <= np; k++) post(total * k / np, S.post_w_m, dc, h * (S.post_top || 1), !!S.post_lid);
  if (ch.style === 'guard') {
    // the W-beam: two faces kinked toward the way (edges post_d_m out, the
    // crest on the line), its top at h
    const b = S.beam_h_m, e = S.post_d_m;
    sweep([[e, h], [0, h - b / 2], [e, h - b]], 'rail');
  } else {
    // two rails (the top one's top at h, one at mid_frac x h): top + both
    // sides of a rail_w_m wide, rail_h_m deep section over the posts' line
    const r = S.rail_w_m / 2, rh = S.rail_h_m;
    for (const yt of [h, h * S.mid_frac + rh / 2]) sweep([[dc - r, yt - rh], [dc - r, yt], [dc + r, yt], [dc + r, yt - rh]], 'rail');
  }
  return n;
}
